import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { offerApi } from '../../services/offerService';
import './PublicOfferPage.css';

const money = (terms = {}) => {
  if (!terms.baseSalary) return 'Not specified';
  return [terms.currency, terms.baseSalary, terms.payFrequency].filter(Boolean).join(' ');
};

const formatDate = (value) => {
  if (!value) return 'Not specified';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PublicOfferPage = () => {
  useUiLanguage();
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [offer, setOffer] = useState(null);
  const [decision, setDecision] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [note, setNote] = useState('');
  const [signatureName, setSignatureName] = useState('');

  const loadOffer = useCallback(async () => {
    if (!token) throw new Error('Missing offer link');
    const payload = await offerApi.resolvePublic(token);
    return payload.offer;
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const nextOffer = await loadOffer();
        if (!cancelled) setOffer(nextOffer);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Unable to load offer');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [loadOffer]);

  const status = String(offer?.status || '').toLowerCase();
  const isResolved = status === 'accepted' || status === 'declined';
  const terms = offer?.terms || {};
  const companyName = offer?.company?.companyName || 'Company';
  const candidateName = offer?.candidate?.name || 'Candidate';
  const jobTitle = offer?.job?.title || offer?.job?.jobTitle || 'Offer';

  const detailRows = [
    ['Role', jobTitle],
    ['Department', offer?.job?.department || 'Not specified'],
    ['Location', offer?.job?.location || 'Not specified'],
    ['Compensation', money(terms)],
    ['Employment type', terms.employmentType || 'Not specified'],
    ['Work mode', terms.workMode || 'Not specified'],
    ['Start date', formatDate(terms.startDate)],
    ['Respond by', formatDate(terms.expiresAt)]
  ];

  const submitDecision = async (action) => {
    setSubmitting(true);
    setError('');
    try {
      if (action === 'decline' && !declineReason.trim()) {
        throw new Error('Please select or enter a decline reason.');
      }
      if (action === 'accept' && !signatureName.trim()) {
        throw new Error('Please type your name to accept the offer.');
      }
      const payload = await offerApi.respondPublic(token, action, {
        declineReason: declineReason.trim(),
        note: note.trim(),
        signatureName: signatureName.trim()
      });
      setOffer(payload.offer);
      setDecision(action);
    } catch (err) {
      setError(err.message || 'Could not submit response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="public-offer-page">
        <section className="public-offer-card public-offer-status">
          <i className="fa-solid fa-spinner fa-spin" />
          <h1>{uiText("Loading Offer")}</h1>
          <p>{uiText("Verifying your secure link.")}</p>
        </section>
      </main>
    );
  }

  if (error && !offer) {
    return (
      <main className="public-offer-page">
        <section className="public-offer-card public-offer-status">
          <i className="fa-solid fa-circle-exclamation" />
          <h1>{uiText("Offer Link Error")}</h1>
          <p>{uiText(error)}</p>
        </section>
      </main>
    );
  }

  return (
    <main className="public-offer-page">
      <section className="public-offer-card">
        <header className="public-offer-header">
          <div>
            <span className="public-offer-company">{companyName}</span>
            <h1>{jobTitle}</h1>
            <p>{uiText("Prepared for ")}{candidateName}</p>
          </div>
          <span className={`public-offer-status-pill ${status || 'sent'}`}>
            {status === 'accepted' ? uiText("Accepted") : status === 'declined' ? uiText("Declined") : uiText("Awaiting response")}
          </span>
        </header>

        {isResolved ? (
          <div className={`public-offer-result ${status}`}>
            <i className={status === 'accepted' ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark'} />
            <div>
              <h2>{status === 'accepted' ? uiText("Offer Accepted") : uiText("Offer Declined")}</h2>
              <p>
                {status === 'accepted'
                  ? uiText("Your response has been recorded. The hiring team will follow up with next steps.")
                  : uiText("Your response has been recorded and shared with the hiring team.")}
              </p>
            </div>
          </div>
        ) : null}

        <div className="public-offer-grid">
          {detailRows.map(([label, value]) => (
            <div className="public-offer-detail" key={label}>
              <span>{uiText(label)}</span>
              <strong>{value}</strong>
            </div>
          ))}
        </div>

        {Array.isArray(offer?.documents?.attachments) && offer.documents.attachments.length > 0 && (
          <section className="public-offer-section">
            <h2>{uiText("Documents")}</h2>
            <div className="public-offer-docs">
              {offer.documents.attachments.map((doc, index) => (
                <a
                  key={`${doc.name}-${index}`}
                  href={doc.url || undefined}
                  target="_blank"
                  rel="noreferrer"
                  className={!doc.url ? 'disabled' : ''}
                >
                  <i className="fa-solid fa-file-lines" />
                  {doc.name || uiText("Offer document")}
                </a>
              ))}
            </div>
          </section>
        )}

        {!isResolved && (
          <section className="public-offer-section">
            <h2>{uiText("Respond")}</h2>
            {error && <div className="public-offer-error">{uiText(error)}</div>}
            <div className="public-offer-response-grid">
              <label>
                <span>{uiText("Type your full name to accept")}</span>
                <input
                  value={signatureName}
                  onChange={(event) => setSignatureName(event.target.value)}
                  placeholder={uiText("Full name")}
                />
              </label>
              <label>
                <span>{uiText("Decline reason")}</span>
                <select value={declineReason} onChange={(event) => setDeclineReason(event.target.value)}>
                  <option value="">{uiText("Select reason")}</option>
                  <option value="compensation">{uiText("Compensation")}</option>
                  <option value="timing">{uiText("Timing / start date")}</option>
                  <option value="role_fit">{uiText("Role fit")}</option>
                  <option value="accepted_other_offer">{uiText("Accepted another offer")}</option>
                  <option value="personal">{uiText("Personal reasons")}</option>
                </select>
              </label>
            </div>
            <label className="public-offer-note">
              <span>{uiText("Optional note")}</span>
              <textarea value={note} onChange={(event) => setNote(event.target.value)} />
            </label>
            <div className="public-offer-actions">
              <button type="button" disabled={submitting} onClick={() => submitDecision('decline')}>
                {submitting && decision === 'decline' ? <i className="fa-solid fa-spinner fa-spin" /> : null}{uiText("Decline")}</button>
              <button type="button" disabled={submitting} className="primary" onClick={() => submitDecision('accept')}>
                {submitting && decision === 'accept' ? <i className="fa-solid fa-spinner fa-spin" /> : null}{uiText("Accept Offer")}</button>
            </div>
          </section>
        )}
      </section>
    </main>
  );
};

export default PublicOfferPage;
